import { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { useAuthStore, useUIStore } from "@/store";
import { MessageSquare, LayoutDashboard, Wifi, Calendar, Users, LogOut, Menu, X, Sun, Moon, FileText, History, User, Coins, Shield, Image, Package, CreditCard, ShoppingCart, DollarSign, Link2, ChevronDown, ChevronRight, RefreshCw, Mail, Server, HeadphonesIcon, Store, Ticket, UserPlus, Send, Bot, Smartphone, HardDrive, Code, Wallet, Bell, Search, Blocks, Gift, Zap, Lightbulb, AlertTriangle } from "lucide-react";
import RenewalModal from "@/components/RenewalModal";
import RenovarPage from "@/pages/RenovarPage";
import ExpirationWarning from "@/components/ExpirationWarning";

// Pages
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import DashboardPage from "@/pages/DashboardPage";
import ConnectionsPage from "@/pages/ConnectionsPage";
import CampaignsPage from "@/pages/CampaignsPage";
import CreateCampaignPage from "@/pages/CreateCampaignPage";
import EditCampaignPage from "@/pages/EditCampaignPage";
import TemplatesPage from "@/pages/TemplatesPage";
import ProfilePage from "@/pages/ProfilePage";
import AllUsersPage from "@/pages/AllUsersPage";
import ResellersPage from "@/pages/ResellersPage";
import HistoryPage from "@/pages/HistoryPage";
import MediaPage from "@/pages/MediaPage";
import BlockedPage from "@/pages/BlockedPage";
import ClientePortalPage from "@/pages/ClientePortalPage";
import DependenciesPage from "@/pages/DependenciesPage";
import PlansPage from "@/pages/PlansPage";
import MyPlansPage from "@/pages/MyPlansPage";
import GatewaysPage from "@/pages/GatewaysPage";
import GerarFaturaPage from "@/pages/GerarFaturaPage";
import FaturaPublicaPage from "@/pages/FaturaPublicaPage";
import CreditShopPage from "@/pages/CreditShopPage";
import CreditPlansAdminPage from "@/pages/CreditPlansAdminPage";
import MasterCreditPlansPage from "@/pages/MasterCreditPlansPage";
import FinancialPage from "@/pages/FinancialPage";
import InviteLinksPage from "@/pages/InviteLinksPage";
import BackupPage from "@/pages/BackupPage";
import ApiIntegrationPage from "@/pages/ApiIntegrationPage";
import NexuPayPage from "@/pages/NexuPayPage";
import NexuPayAdminPage from "@/pages/NexuPayAdminPage";
import CreditStorePage from "@/pages/CreditStorePage";
import CreditStorePublicPage from "@/pages/CreditStorePublicPage";
import CustomersPage from "@/pages/CustomersPage";
import RecoveryPage from "@/pages/RecoveryPage";
import CouponsPage from "@/pages/CouponsPage";
import SMTPConfigPage from "@/pages/SMTPConfigPage";
import LogoConfigPage from "@/pages/LogoConfigPage";
import CustomerPaymentPage from "@/pages/CustomerPaymentPage";
import SigmaServersPage from "@/pages/SigmaServersPage";
import IntegrationsPage from "@/pages/IntegrationsPage";
import SupportPage from "@/pages/SupportPage";
import AutomationsPage from "@/pages/AutomationsPage";
import BulkSendPage from "@/pages/BulkSendPage";
import GroupMemberManagerPage from "@/pages/GroupMemberManagerPage";
import GroupLinksPage from "@/pages/GroupLinksPage";
import ReplicatorPage from "@/pages/ReplicatorPage";
import StoreSettingsPage from "@/pages/StoreSettingsPage";
import VendedoresPage from "@/pages/VendedoresPage";
import VendedorClientesPage from "@/pages/VendedorClientesPage";
import PublicStorePage from "@/pages/PublicStorePage";
import GroupManagementPage from "@/pages/GroupManagementPage";
import AppConfigPage from "@/pages/AppConfigPage";
import useVersionCheck from "@/hooks/useVersionCheck";
import GlobalSearch from "@/components/GlobalSearch";
import BulkBillingIndicator from "@/components/BulkBillingIndicator";
import NexuVoiceOverlay from "@/components/NexuVoiceOverlay";
import AIAssistantPage from "@/pages/AIAssistantPage";
import ExternalsPage from "@/pages/ExternalsPage";
import NexuPage from "@/pages/NexuPage";

// Theme hook
const useTheme = () => {
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') || 'dark';
    }
    return 'dark';
  });

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Troca de tema com animação de "bolha subindo": o tema novo nasce embaixo, na altura do clique,
  // e cresce pintando a tela até cobrir tudo (View Transitions API; sem suporte, troca direto).
  const toggleTheme = (event) => {
    const root = window.document.documentElement;
    const next = root.classList.contains('dark') ? 'light' : 'dark';
    const apply = () => {
      root.classList.remove('light', 'dark');
      root.classList.add(next);
      flushSync(() => setTheme(next));
    };

    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (!document.startViewTransition || reduceMotion) {
      apply();
      return;
    }

    const w = window.innerWidth;
    const h = window.innerHeight;
    const x = event?.clientX ?? w / 2;
    const y = h + 40;                                   // nasce abaixo da borda da tela
    const radius = Math.hypot(Math.max(x, w - x), y);   // até cobrir o canto mais distante

    root.classList.add('theme-switching');
    const transition = document.startViewTransition(apply);
    transition.ready.then(() => {
      // bolha: começa estreita e alta (gota subindo) e termina redonda cobrindo tudo
      root.animate(
        {
          clipPath: [
            `ellipse(0px 0px at ${x}px ${y}px)`,
            `ellipse(${radius * 0.18}px ${radius * 0.42}px at ${x}px ${y}px)`,
            `ellipse(${radius * 1.05}px ${radius * 1.05}px at ${x}px ${y}px)`,
          ],
          offset: [0, 0.35, 1],
        },
        { duration: 950, easing: 'cubic-bezier(0.7, 0, 0.2, 1)', pseudoElement: '::view-transition-new(root)', fill: 'both' }
      );
      // o tema antigo "afunda" levemente enquanto a bolha passa
      root.animate(
        { transform: ['scale(1)', 'scale(0.97)'], filter: ['brightness(1)', 'brightness(0.8)'] },
        { duration: 950, easing: 'ease-in', pseudoElement: '::view-transition-old(root)', fill: 'both' }
      );
    }).catch(() => {});
    transition.finished.finally(() => root.classList.remove('theme-switching'));
  };

  return { theme, toggleTheme };
};

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false, masterOrAdmin = false, masterOnly = false, allRoles = false, notSimple = false }) => {
  const { isAuthenticated, user, checkAuth } = useAuthStore();
  const [checking, setChecking] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const verify = async () => {
      const valid = await checkAuth();
      if (!valid) {
        navigate('/login');
      }
      setChecking(false);
    };
    verify();
  }, [checkAuth, navigate]);

  if (checking) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <span className="text-primary font-mono text-sm">Carregando...</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Verificar se conta expirada (vendedores não têm expiração própria)
  if (user?.role !== 'admin' && user?.role !== 'vendedor' && user?.expires_at) {
    if (new Date(user.expires_at) < new Date()) {
      return <Navigate to="/blocked" state={{ reason: 'expired' }} replace />;
    }
  }

  if (adminOnly && user?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  if (masterOrAdmin && user?.role !== 'admin' && user?.role !== 'master') {
    return <Navigate to="/dashboard" replace />;
  }

  if (masterOnly && user?.role !== 'master') {
    return <Navigate to="/dashboard" replace />;
  }

  // allRoles permite admin, master, reseller, simple e vendedor
  if (allRoles && !['admin', 'master', 'reseller', 'simple', 'vendedor'].includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  if (notSimple && (user?.role === 'simple' || user?.role === 'vendedor')) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Layout Component with Responsive Sidebar
const Layout = ({ children }) => {
  const { user, logout } = useAuthStore();
  const { sidebarOpen, setSidebarOpen } = useUIStore();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState({});
  const [customLogo, setCustomLogo] = useState(null);
  const [unreadTickets, setUnreadTickets] = useState(0);
  const [panelNotifs, setPanelNotifs] = useState([]);
  const [panelUnread, setPanelUnread] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  
  // Verificação de versão para atualizações
  useVersionCheck();

  // Buscar contador de tickets não lidos
  useEffect(() => {
    const fetchUnreadTickets = async () => {
      try {
        const token = localStorage.getItem('nexus-token');
        if (!token) return;
        
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${API_URL}/api/tickets/unread-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setUnreadTickets(data.count || 0);
        }
      } catch (error) {
        console.error('Erro ao buscar tickets:', error);
      }
    };
    
    fetchUnreadTickets();
    const interval = setInterval(fetchUnreadTickets, 30000);
    return () => clearInterval(interval);
  }, [user]);

  // Buscar notificações do painel
  useEffect(() => {
    const fetchPanelNotifs = async () => {
      try {
        const token = localStorage.getItem('nexus-token');
        if (!token) return;
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${API_URL}/api/panel-notifications`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setPanelNotifs(data.notifications || []);
          setPanelUnread(data.unread || 0);
        }
      } catch {}
    };
    fetchPanelNotifs();
    const interval = setInterval(fetchPanelNotifs, 20000);
    return () => clearInterval(interval);
  }, [user]);

  const markAllNotifsRead = async () => {
    try {
      const token = localStorage.getItem('nexus-token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API_URL}/api/panel-notifications/read-all`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      setPanelUnread(0);
      setPanelNotifs(prev => prev.map(n => ({ ...n, read: true })));
    } catch {}
  };

  // Buscar logo personalizada
  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${API_URL}/api/settings/logo`);
        if (response.ok) {
          const data = await response.json();
          if (data.logo_url) {
            setCustomLogo(data.logo_url);
          }
        }
      } catch (error) {
        console.error('Erro ao buscar logo:', error);
      }
    };
    fetchLogo();
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNavigation = (path) => {
    navigate(path);
    setSidebarOpen(false);
  };

  const toggleMenu = (menuKey) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuKey]: !prev[menuKey]
    }));
  };

  // Função para obter saudação baseada na hora
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) return 'Bom dia';
    if (hour >= 12 && hour < 18) return 'Boa tarde';
    return 'Boa noite';
  };

  // Seções de navegação organizadas
  const navSections = [];
  const isSimple = user?.role === 'simple';
  const isVendedor = user?.role === 'vendedor';

  // VENDEDOR: sidebar minimalista
  if (isVendedor) {
    navSections.push({
      title: null,
      items: [
        { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
      ]
    });
    navSections.push({
      title: 'Gerenciamento',
      items: [
        { path: '/customers', label: 'Gestor de Clientes', icon: Users },
      ]
    });
    navSections.push({
      title: 'Configuração',
      items: [
        { path: '/app-config', label: 'App | Config', icon: Smartphone },
      ]
    });
    navSections.push({
      title: 'Financeiro',
      items: [
        { path: '/nexupay', label: 'NexuPay', icon: Wallet },
      ]
    });
  } else {

  // SEÇÃO: Principal
  navSections.push({
    title: null, // Sem título para a primeira seção
    items: [
      { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    ]
  });

  // SEÇÃO: Gerenciamento (filtrado por role)
  const gerenciamentoItems = [
    { path: '/customers', label: 'Gestor de Clientes', icon: Users },
  ];
  if (!isSimple) {
    gerenciamentoItems.unshift({ path: '/connections', label: 'Conexões', icon: Wifi });
    gerenciamentoItems.push(
      { path: '/campaigns', label: 'Campanhas', icon: Calendar },
      { path: '/automations', label: 'Automações', icon: MessageSquare },
      { path: '/ai-assistant', label: 'IA de Atendimento', icon: Bot },
      { path: '/group-management', label: 'Gestão de Grupos', icon: Shield },
    );
  } else {
    // Nexu Simple: acessa a IA de Atendimento só para configurar a chave Gemini (usada pelo NEXU)
    gerenciamentoItems.push({ path: '/ai-assistant', label: 'IA de Atendimento', icon: Bot });
  }
  navSections.push({
    title: 'Gerenciamento',
    items: gerenciamentoItems
  });

  // SEÇÃO: App Config (para todos, destaque no Simple)
  navSections.push({
    title: isSimple ? 'Configuração' : null,
    items: [
      { path: '/app-config', label: 'App | Config', icon: Smartphone },
    ]
  });

  if (!isSimple) {
    // SEÇÃO: Ferramentas
    const ferramentasItems = [
      { path: '/bulk-send', label: 'Envio em Massa', icon: Send, risco: true },
      { path: '/group-member-manager', label: 'Extrair | Enviar', icon: UserPlus, risco: true },
      { path: '/recovery', label: 'Recuperação', icon: UserPlus },
      { path: '/coupons', label: 'Cupons de Desconto', icon: Ticket },
      { path: '/replicator', label: 'Replicador', icon: RefreshCw },
    ];
    if (user?.role === 'admin' || user?.role === 'master' || user?.role === 'reseller') {
      ferramentasItems.push({ path: '/gerar-fatura', label: 'Gerar Fatura', icon: FileText });
    }
    navSections.push({
      title: 'Ferramentas',
      items: ferramentasItems
    });

    // SEÇÃO: Conteúdo
    navSections.push({
      title: 'Conteúdo',
      items: [
        { path: '/templates', label: 'Templates', icon: FileText },
        { path: '/media', label: 'Mídias', icon: Image },
      ]
    });

    // SEÇÃO: Histórico
    navSections.push({
      title: 'Relatórios',
      items: [
        { path: '/history', label: 'Histórico', icon: History },
      ]
    });
  }

  // SEÇÃO: Minha Conta (para master, resellers e simple)
  if (user?.role === 'master' || user?.role === 'reseller' || user?.role === 'simple') {
    const contaItems = [
      { path: '/my-plans', label: 'Renovar Painel', icon: RefreshCw },
    ];
    // Gateways para todos (master, reseller e simple)
    contaItems.push({ path: '/gateways', label: 'Gateways', icon: CreditCard });
    navSections.push({
      title: 'Minha Conta',
      items: contaItems
    });
  }

  // SEÇÃO: Financeiro (para master/admin/simple/reseller)
  if (user?.role === 'admin' || user?.role === 'master' || user?.role === 'simple' || user?.role === 'reseller') {
    const financeiroItems = [];
    
    if (user?.role === 'admin' || user?.role === 'master') {
      financeiroItems.push({ path: '/financial', label: 'Transações', icon: DollarSign });
      financeiroItems.push({ path: '/gateways', label: 'Gateways', icon: CreditCard });
    }
    if (user?.role === 'simple') {
      financeiroItems.push({ path: '/financial', label: 'Transações', icon: DollarSign });
    }
    if (user?.role === 'reseller') {
      financeiroItems.push({ path: '/financial', label: 'Transações', icon: DollarSign });
      financeiroItems.push({ path: '/gateways', label: 'Gateways', icon: CreditCard });
    }
    
    if (user?.role === 'master' || user?.role === 'simple') {
      financeiroItems.push({ path: '/credit-shop', label: 'Loja de Créditos', icon: ShoppingCart });
    }

    // NexuPay para todos
    financeiroItems.push({ path: '/nexupay', label: 'NexuPay', icon: Wallet });
    
    navSections.push({
      title: 'Financeiro',
      items: financeiroItems
    });
  }

  // SEÇÃO: Gestão (para master/admin/simple)
  if (user?.role === 'admin' || user?.role === 'master' || user?.role === 'simple') {
    const gestaoItems = [
      { path: '/resellers', label: 'Revendedores', icon: Users },
      { path: '/invite-links', label: 'Links de Convite', icon: Link2 },
    ];
    if (user?.role !== 'simple') {
      gestaoItems.push({ path: '/plans', label: 'Planos', icon: Package });
    } else {
      gestaoItems.push({ path: '/plans', label: 'Planos Simple', icon: Package });
    }
    
    navSections.push({
      title: 'Gestão',
      items: gestaoItems
    });
  }

  // SEÇÃO: Meus Pacotes (master e simple podem vender créditos)
  if (user?.role === 'master' || user?.role === 'simple') {
    navSections.push({
      title: 'Vendas',
      items: [
        { path: '/my-credit-plans', label: 'Meus Pacotes de Créditos', icon: Package },
      ]
    });
  }

  // SEÇÃO: Integrações (para todos exceto simple)
  if (user?.role === 'master' || user?.role === 'admin' || user?.role === 'reseller') {
    const integracoesItems = [
      { path: '/externals', label: 'Externos', icon: Blocks },
      { path: '/integrations', label: 'Minhas Integrações', icon: Link2 },
    ];
    
    // Loja de Créditos apenas para master e admin
    if (user?.role === 'master' || user?.role === 'admin') {
      integracoesItems.push({ path: '/loja-creditos', label: 'Loja de Créditos', icon: Store });
    }
    integracoesItems.push({ path: '/support', label: 'Suporte', icon: HeadphonesIcon });
    integracoesItems.push({ path: '/backup', label: 'Backup', icon: HardDrive });
    integracoesItems.push({ path: '/api-integration', label: 'API de Integração', icon: Code });
    
    navSections.push({
      title: 'Integrações',
      items: integracoesItems
    });
  }

  // Ferramentas para Simple
  if (isSimple) {
    navSections.push({
      title: 'Ferramentas',
      items: [
        { path: '/integrations', label: 'Minhas Integrações', icon: Link2 },
        { path: '/backup', label: 'Backup', icon: HardDrive },
        { path: '/api-integration', label: 'API de Integração', icon: Code },
        { path: '/support', label: 'Suporte', icon: HeadphonesIcon },
      ]
    });
  }

  // SEÇÃO: Administração (apenas admin)
  if (user?.role === 'admin' || user?.role === 'Administrador') {
    navSections.push({
      title: 'Administração',
      items: [
        { path: '/group-links', label: 'Links de Grupos', icon: Link2 },
        { path: '/credit-plans', label: 'Pacotes de Créditos', icon: Coins },
        { path: '/users', label: 'Todos Usuários', icon: Shield },
        { path: '/sigma-servers', label: 'Servidores Sigma', icon: Server },
        { path: '/smtp-config', label: 'Config. SMTP', icon: Mail },
        { path: '/logo-config', label: 'Logo do Sistema', icon: Image },
        { path: '/nexupay-admin', label: 'NexuPay Admin', icon: Wallet },
      ]
    });
  }

  } // end of non-vendedor else block

  // SEÇÃO: NEXU IA — assistente (todos menos vendedor)
  if (user?.role !== 'vendedor') {
    navSections.push({
      title: 'Assistente',
      items: [{ path: '/nexu', label: 'NEXU IA', icon: Zap }],
    });
  }

  // SEÇÃO: Indique e Ganhe — visível para TODOS os perfis
  navSections.push();

  // SEÇÃO: Vendedores (para simple/master/admin que podem criar vendedores)
  if (user?.role === 'simple' || user?.role === 'master' || user?.role === 'admin') {
    // Adicionar depois da seção de Gestão
    const gestaoSection = navSections.find(s => s.title === 'Gestão');
    if (gestaoSection) {
      gestaoSection.items.push({ path: '/vendedores', label: 'Vendedores', icon: Users });
    }
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Subtle background effects for dashboard */}
      <div className="fixed inset-0 cyber-dots opacity-30 pointer-events-none" />
      
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-14 glass border-b border-white/5 z-50 flex items-center justify-between px-4 safe-top">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 -ml-2 text-muted-foreground hover:text-foreground transition-colors"
          data-testid="mobile-menu-btn"
        >
          <Menu className="w-6 h-6" />
        </button>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center shadow-lg shadow-primary/20">
            <MessageSquare className="w-4 h-4 text-primary-foreground" />
          </div>
          <span className="font-heading font-bold text-lg text-foreground">NEXU<span className="text-primary">ZAP</span></span>
        </div>
        <div className="flex items-center gap-2">
          {/* Credits badge for mobile */}
          {(user?.role === 'admin' || user?.role === 'master' || user?.role === 'simple') && (
            <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-yellow-500/15 text-yellow-500 text-xs font-medium border border-yellow-500/20">
              <Coins className="w-3 h-3" />
              {user?.role === 'admin' ? '∞' : (user?.credits || 0)}
            </div>
          )}
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('open-global-search'))}
            className="p-2 text-muted-foreground hover:text-primary transition-colors"
            title="Buscar (Ctrl+K)"
          >
            <Search className="w-5 h-5" />
          </button>
          <button
            onClick={() => setNotifOpen(!notifOpen)}
            className="p-2 text-muted-foreground hover:text-primary transition-colors relative"
            data-testid="notif-bell-mobile"
          >
            <Bell className="w-5 h-5" />
            {panelNotifs.filter(n => !n.read).length > 0 && <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-red-500 rounded-full border border-background" />}
          </button>
          <button
            onClick={toggleTheme}
            className="p-2 -mr-2 text-muted-foreground hover:text-primary transition-colors"
          >
            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
        </div>
      </header>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/70 z-50 backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-72 glass-panel border-r border-white/5 z-50
        transform transition-transform duration-300 ease-out
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-4 lg:p-6 flex items-center justify-center flex-shrink-0">
            {customLogo ? (
              <img 
                src={customLogo} 
                alt="Logo" 
                className="h-10 max-w-[180px] object-contain"
              />
            ) : (
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="absolute inset-0 bg-primary/30 rounded-xl blur-lg animate-glow-pulse" />
                  <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center neon-glow">
                    <MessageSquare className="w-5 h-5 text-primary-foreground" />
                  </div>
                </div>
                <div>
                  <h1 className="font-heading font-bold text-xl text-foreground tracking-tight">NEXU<span className="text-primary">ZAP</span></h1>
                  <p className="text-[10px] font-mono text-primary/70 uppercase tracking-[0.2em]">Gestao de Clientes</p>
                </div>
              </div>
            )}
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation - com scroll */}
          <nav className="flex-1 px-3 py-2 overflow-y-auto no-scrollbar">
            {/* Resumo da conta: dias restantes em destaque */}
            {(() => {
              const isAdmin = user?.role === 'admin';
              const showValidity = !isAdmin && user?.role !== 'vendedor' && user?.expires_at;
              const showCredits = user?.role === 'master' || user?.role === 'simple';
              const exp = user?.expires_at ? new Date(user.expires_at) : null;
              const msDay = 24 * 60 * 60 * 1000;
              const daysLeft = exp ? Math.ceil((exp.getTime() - Date.now()) / msDay) : null;
              const expired = daysLeft !== null && daysLeft < 0;
              const tone = expired || daysLeft === 0 ? 'red' : daysLeft !== null && daysLeft <= 7 ? 'amber' : 'green';
              const toneText = { red: 'text-red-400', amber: 'text-amber-400', green: 'text-primary' }[tone];
              const toneBar = { red: 'bg-red-500', amber: 'bg-amber-400', green: 'bg-primary' }[tone];
              const pct = daysLeft === null ? 0 : Math.max(expired ? 100 : 4, Math.min(100, (daysLeft / 30) * 100));
              const firstName = (user?.username || '').split('@')[0];

              return (
                <div className="mb-5 px-4 pt-3 pb-4 rounded-2xl bg-foreground/[0.03] dark:bg-white/[0.03]" data-testid="account-summary">
                  <div className="flex items-baseline justify-between gap-2">
                    <p className="text-sm text-muted-foreground truncate">
                      {getGreeting()}{firstName ? <>, <span className="text-foreground font-medium">{firstName}</span></> : ''}
                    </p>
                    {user?.panel_id && !isAdmin && (
                      <button
                        type="button"
                        title="Copiar ID"
                        onClick={() => { navigator.clipboard?.writeText(user.panel_id); toast.success('ID copiado'); }}
                        className="shrink-0 text-[11px] text-muted-foreground/70 hover:text-foreground transition-colors tabular-nums"
                      >
                        {user.panel_id}
                      </button>
                    )}
                  </div>

                  {isAdmin && (
                    <p className="mt-2 text-xs text-primary font-medium">Administrador</p>
                  )}

                  {showValidity && (
                    <>
                      <div className="mt-3 flex items-baseline gap-2">
                        <span className={`text-4xl font-bold leading-none tabular-nums ${toneText}`}>
                          {expired ? Math.abs(daysLeft) : daysLeft}
                        </span>
                        <span className="text-sm text-muted-foreground leading-tight">
                          {expired
                            ? `dia${Math.abs(daysLeft) === 1 ? '' : 's'} vencida`
                            : daysLeft === 0 ? 'vence hoje' : `dia${daysLeft === 1 ? '' : 's'} restante${daysLeft === 1 ? '' : 's'}`}
                        </span>
                      </div>
                      <div className="mt-3 h-1 rounded-full bg-foreground/10 overflow-hidden" role="progressbar"
                        aria-valuenow={Math.max(0, daysLeft)} aria-valuemin={0} aria-valuemax={30} aria-label="Dias até o vencimento">
                        <div className={`h-full rounded-full ${toneBar} transition-[width] duration-700`} style={{ width: `${pct}%` }} />
                      </div>
                    </>
                  )}

                  {(showValidity || showCredits) && (
                    <div className="mt-2.5 flex items-center justify-between text-xs text-muted-foreground">
                      {showValidity ? <span>{expired ? 'Venceu' : 'Vence'} em {exp.toLocaleDateString('pt-BR')}</span> : <span />}
                      {showCredits && (
                        <span className="flex items-center gap-1 tabular-nums">
                          <Coins className="w-3.5 h-3.5 text-amber-400" />
                          <span className="text-foreground font-medium">{user?.credits || 0}</span> crédito{(user?.credits || 0) === 1 ? '' : 's'}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Nav Sections */}
            {navSections.map((section, sectionIndex) => (
              <div key={sectionIndex} className={section.title ? 'mt-5 first:mt-0' : ''}>
                {section.title && (
                  <p className="px-4 py-2 text-[10px] font-bold text-muted-foreground/60 uppercase tracking-[0.2em]">
                    {section.title}
                  </p>
                )}
                <div className="space-y-1">
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    const showBadge = item.showBadge && unreadTickets > 0;
                    return (
                      <button
                        key={item.path}
                        onClick={() => handleNavigation(item.path)}
                        data-testid={`nav-${item.path.slice(1)}`}
                        className={`
                          w-full flex items-center gap-3 px-4 py-2.5 rounded-xl
                          transition-all duration-200 touch-target group
                          ${isActive 
                            ? (item.destaque ? 'bg-amber-500/20 text-amber-500 border border-amber-500/40 shadow-lg shadow-amber-500/10' : 'bg-primary/15 text-primary border border-primary/30 shadow-lg shadow-primary/10')
                            : (item.destaque ? 'text-amber-500 bg-gradient-to-r from-amber-500/10 to-orange-500/5 border border-amber-500/25 hover:from-amber-500/20 hover:border-amber-500/40' : 'text-muted-foreground hover:text-foreground hover:bg-white/5 border border-transparent')
                          }
                        `}
                      >
                        <Icon className={`w-5 h-5 flex-shrink-0 transition-transform duration-200 ${isActive ? '' : 'group-hover:scale-110'}`} strokeWidth={1.5} />
                        <span className="font-medium text-sm flex-1 text-left">{item.label}</span>
                        {item.risco && (
                          <span title="Uso com moderação: risco de banimento do WhatsApp" className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-red-500/15 text-red-400 border border-red-500/25 text-[9px] font-bold uppercase tracking-wide">
                            <AlertTriangle className="w-2.5 h-2.5" /> Risco
                          </span>
                        )}
                        {item.isNew && (
                          <span className="px-2 py-0.5 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 text-white text-[10px] font-bold uppercase tracking-wider animate-pulse shadow-lg shadow-green-500/30">
                            Novo
                          </span>
                        )}
                        {showBadge && (
                          <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-bold animate-pulse">
                            {unreadTickets > 9 ? '+9' : unreadTickets}
                          </span>
                        )}
                        {isActive && (
                          <div className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          {/* Theme + Notifications side by side (Desktop) */}
          <div className="hidden lg:flex gap-2 px-4 pb-2 mt-auto">
            <button
              onClick={toggleTheme}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs text-muted-foreground hover:text-foreground hover:bg-white/5 transition-all duration-200 border border-transparent hover:border-white/10"
            >
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              {theme === 'dark' ? 'Claro' : 'Escuro'}
            </button>
            <button
              onClick={() => setNotifOpen(!notifOpen)}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs text-muted-foreground hover:text-foreground hover:bg-white/5 transition-all duration-200 border border-transparent hover:border-white/10 relative"
              data-testid="notif-bell-desktop"
            >
              <Bell className="w-4 h-4" />
              Alertas
              {panelNotifs.filter(n => !n.read).length > 0 && <span className="ml-0.5 px-1.5 py-0.5 text-[10px] font-bold bg-red-500 text-white rounded-full">{panelNotifs.filter(n => !n.read).length}</span>}
            </button>
          </div>

          {/* User Section */}
          <div className="p-4 border-t border-white/5 safe-bottom">
            <button 
              onClick={() => handleNavigation('/profile')}
              className="w-full flex items-center gap-3 mb-3 p-2 -m-2 rounded-xl hover:bg-white/5 transition-all duration-200 group"
            >
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/30 to-primary/10 flex items-center justify-center flex-shrink-0 border border-primary/20 group-hover:border-primary/40 transition-colors">
                <span className="font-heading font-bold text-primary uppercase">
                  {user?.username?.[0] || 'U'}
                </span>
              </div>
              <div className="flex-1 min-w-0 text-left">
                <p className="font-medium text-sm text-foreground truncate">{user?.username}</p>
                <p className="text-xs font-mono text-primary/70 uppercase tracking-wider">
                  {user?.role === 'admin' ? 'Admin' : user?.role === 'master' ? 'Master' : user?.role === 'simple' ? 'Nexu Simple' : user?.role === 'vendedor' ? 'Vendedor' : 'Revenda'}
                </p>
              </div>
              <User className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
            </button>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-all duration-200 border border-transparent hover:border-red-500/20 touch-target"
            >
              <LogOut className="w-4 h-4" />
              Sair
            </button>
          </div>
        </div>
      </aside>

      {/* Panel Notifications Dropdown */}
      {notifOpen && (
        <div className="fixed inset-0 z-[60]" onClick={() => setNotifOpen(false)}>
          <div
            className="absolute right-4 top-16 lg:left-72 lg:right-auto lg:top-auto lg:bottom-24 w-80 bg-background border border-border rounded-xl shadow-2xl overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-3 border-b border-border flex items-center justify-between">
              <p className="text-sm font-semibold">Notificacoes</p>
              {panelNotifs.filter(n => !n.read).length > 0 && <button onClick={markAllNotifsRead} className="text-[10px] text-primary hover:underline">Marcar todas como lidas</button>}
            </div>
            <div className="overflow-y-auto" style={{ maxHeight: '210px' }}>
              {panelNotifs.filter(n => !n.read).length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">Nenhuma notificacao</p>
              ) : panelNotifs.filter(n => !n.read).slice(0, 3).map(n => (
                <div key={n.id} className="px-3 py-2.5 border-b border-border/50 bg-primary/5">
                  <p className="text-xs font-semibold">{n.title}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{n.message}</p>
                  <p className="text-[9px] text-muted-foreground mt-1">{new Date(n.created_at).toLocaleString('pt-BR')}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="lg:ml-72 min-h-screen pt-14 lg:pt-0">
        <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto">
          <ExpirationWarning />
          {children}
        </div>
      </main>

      <GlobalSearch />
      <BulkBillingIndicator />
      <NexuVoiceOverlay />
      
      {/* Modals */}
      <RenewalModal />
    </div>
  );
};

function App() {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Set initial theme
    const savedTheme = localStorage.getItem('theme') || 'dark';
    document.documentElement.classList.add(savedTheme);
    setMounted(true);
  }, []);

  if (!mounted) {
    return null;
  }

  return (
    <BrowserRouter>
      <Routes>
        {/* Rota pública - Link de pagamento do cliente */}
        <Route path="/pagamento/:customerId" element={<CustomerPaymentPage />} />
        <Route path="/renovar/:token" element={<RenovarPage />} />
        <Route path="/fatura/:faturaId" element={<FaturaPublicaPage />} />
        <Route path="/loja/:ownerId" element={<CreditStorePublicPage />} />
        
        {/* Rota pública - Portal do Cliente (versão web do app mobile) */}
        <Route path="/cliente/*" element={<ClientePortalPage />} />
        
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register/:inviteCode" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/blocked" element={<BlockedPage />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Layout>
                <DashboardPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/renovar"
          element={
            <ProtectedRoute>
              <RenovarPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/connections"
          element={
            <ProtectedRoute notSimple>
              <Layout>
                <ConnectionsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/campaigns"
          element={
            <ProtectedRoute>
              <Layout>
                <CampaignsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/customers"
          element={
            <ProtectedRoute>
              <Layout>
                <CustomersPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/recovery"
          element={
            <ProtectedRoute>
              <Layout>
                <RecoveryPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/coupons"
          element={
            <ProtectedRoute>
              <Layout>
                <CouponsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/campaigns/new"
          element={
            <ProtectedRoute>
              <Layout>
                <CreateCampaignPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/campaigns/edit/:id"
          element={
            <ProtectedRoute>
              <Layout>
                <EditCampaignPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/automations"
          element={
            <ProtectedRoute>
              <Layout>
                <AutomationsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-assistant"
          element={
            <ProtectedRoute>
              <Layout>
                <AIAssistantPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/externals"
          element={
            <ProtectedRoute notSimple>
              <Layout>
                <ExternalsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/nexu"
          element={
            <ProtectedRoute>
              <Layout>
                <NexuPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/group-management"
          element={
            <ProtectedRoute>
              <Layout>
                <GroupManagementPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/bulk-send"
          element={
            <ProtectedRoute>
              <Layout>
                <BulkSendPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/group-member-manager"
          element={
            <ProtectedRoute>
              <Layout>
                <GroupMemberManagerPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/group-links"
          element={
            <ProtectedRoute>
              <Layout>
                <GroupLinksPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/templates"
          element={
            <ProtectedRoute>
              <Layout>
                <TemplatesPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/media"
          element={
            <ProtectedRoute>
              <Layout>
                <MediaPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/history"
          element={
            <ProtectedRoute>
              <Layout>
                <HistoryPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Layout>
                <ProfilePage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/app-config"
          element={
            <ProtectedRoute>
              <Layout>
                <AppConfigPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/resellers"
          element={
            <ProtectedRoute allRoles>
              <Layout>
                <ResellersPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/vendedores"
          element={
            <ProtectedRoute allRoles>
              <Layout>
                <VendedoresPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/vendedores/:vendedorId/clientes"
          element={
            <ProtectedRoute allRoles>
              <Layout>
                <VendedorClientesPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute adminOnly>
              <Layout>
                <AllUsersPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dependencies"
          element={
            <ProtectedRoute adminOnly>
              <Layout>
                <DependenciesPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/plans"
          element={
            <ProtectedRoute allRoles>
              <Layout>
                <PlansPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-plans"
          element={
            <ProtectedRoute>
              <Layout>
                <MyPlansPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/gateways"
          element={
            <ProtectedRoute>
              <Layout>
                <GatewaysPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/gerar-fatura"
          element={
            <ProtectedRoute>
              <Layout>
                <GerarFaturaPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/credit-plans"
          element={
            <ProtectedRoute adminOnly>
              <Layout>
                <CreditPlansAdminPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-credit-plans"
          element={
            <ProtectedRoute allRoles>
              <Layout>
                <MasterCreditPlansPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/smtp-config"
          element={
            <ProtectedRoute adminOnly>
              <Layout>
                <SMTPConfigPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/logo-config"
          element={
            <ProtectedRoute adminOnly>
              <Layout>
                <LogoConfigPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/credit-shop"
          element={
            <ProtectedRoute allRoles>
              <Layout>
                <CreditShopPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/financial"
          element={
            <ProtectedRoute allRoles>
              <Layout>
                <FinancialPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/invite-links"
          element={
            <ProtectedRoute allRoles>
              <Layout>
                <InviteLinksPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/integrations"
          element={
            <ProtectedRoute>
              <Layout>
                <IntegrationsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/store-settings"
          element={
            <ProtectedRoute masterOrAdmin>
              <Layout>
                <StoreSettingsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/support"
          element={
            <ProtectedRoute>
              <Layout>
                <SupportPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/backup"
          element={
            <ProtectedRoute>
              <Layout>
                <BackupPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/api-integration"
          element={<ProtectedRoute><Layout><ApiIntegrationPage /></Layout></ProtectedRoute>}
        />
        <Route
          path="/nexupay"
          element={<ProtectedRoute><Layout><NexuPayPage /></Layout></ProtectedRoute>}
        />
        <Route
          path="/nexupay-admin"
          element={<ProtectedRoute adminOnly><Layout><NexuPayAdminPage /></Layout></ProtectedRoute>}
        />
        <Route
          path="/loja-creditos"
          element={<ProtectedRoute masterOrAdmin><Layout><CreditStorePage /></Layout></ProtectedRoute>}
        />

        <Route
          path="/sigma-servers"
          element={
            <ProtectedRoute adminOnly>
              <Layout>
                <SigmaServersPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/replicator"
          element={
            <ProtectedRoute>
              <Layout>
                <ReplicatorPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        {/* Rota pública da loja */}
        <Route path="/loja/:storeSlug" element={<PublicStorePage />} />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
      <Toaster richColors position="top-center" />
    </BrowserRouter>
  );
}

export default App;
